// providers/lectureDocumentSessionRequest.ts
//
// The `/document/:lectureId?page=N` address (ORA-736). The page behind it is
// gone; the address stays because citation targets, old links, emails and a
// study session's way back use it. Its loader redirects to the Dashboard with
// the request in the query (`/dashboard?document=<lectureId>&page=N`), where
// the companion is available, and the document viewer provider starts a
// lecture document session for it there — exactly as View Document would —
// and takes the params back off the address.
//
// The request travels in the URL rather than in memory so it survives what a
// full page load does to memory: a logged-out student is sent to sign in with
// the address as `?redirect=`, and an OAuth sign-in comes back through
// `window.location.replace`. Nothing outlives the address, so a request can
// never replay later (after a sign-out, for another account).

import { redirect } from 'react-router-dom';

export interface LectureDocumentSessionRequest {
  lectureId: string;
  page: number | null;
}

/** Where the redirect lands and the session is started. */
export const LECTURE_DOCUMENT_SESSION_PATH = '/dashboard';

const DOCUMENT_PARAM = 'document';
const PAGE_PARAM = 'page';

/** The page number in `?page=`, or null for anything but a positive integer. */
export const parseDocumentPage = (raw: string | null): number | null => {
  if (!raw || !/^\d+$/.test(raw)) return null;
  const page = Number(raw);
  return Number.isSafeInteger(page) && page > 0 ? page : null;
};

/** The deep link that reopens a lecture's document session at a page. */
export const lectureDocumentPath = (
  lectureId: string,
  page: number | null = null
): string =>
  `/document/${encodeURIComponent(lectureId)}${page ? `?${PAGE_PARAM}=${page}` : ''}`;

/** The session request a Dashboard address carries, if any. */
export const readLectureDocumentSessionRequest = (
  search: string
): LectureDocumentSessionRequest | null => {
  const params = new URLSearchParams(search);
  const lectureId = params.get(DOCUMENT_PARAM);
  if (!lectureId) return null;

  return { lectureId, page: parseDocumentPage(params.get(PAGE_PARAM)) };
};

/** The query with the session request taken off (`''` when nothing is left). */
export const withoutLectureDocumentSessionRequest = (
  search: string
): string => {
  const params = new URLSearchParams(search);
  params.delete(DOCUMENT_PARAM);
  params.delete(PAGE_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
};

/**
 * The `/document/:lectureId` route's loader: redirect to the Dashboard with
 * the session request in the query. A loader, like the `/chat` redirect, so
 * nothing paints first.
 */
export const documentRedirectLoader = ({
  params,
  request,
}: {
  params: { lectureId?: string };
  request: Request;
}) => {
  const { lectureId } = params;
  if (!lectureId) return redirect(LECTURE_DOCUMENT_SESSION_PATH);

  const query = new URLSearchParams({ [DOCUMENT_PARAM]: lectureId });

  const page = parseDocumentPage(
    new URL(request.url).searchParams.get(PAGE_PARAM)
  );

  if (page) query.set(PAGE_PARAM, String(page));

  return redirect(`${LECTURE_DOCUMENT_SESSION_PATH}?${query.toString()}`);
};
